import { ReactNode } from 'react';
import { ProgressBar, Spinner } from '@fluentui/react-components';
import { useQuery } from '@tanstack/react-query';

import {
  LicenseBalance,
  getLicenseBalance,
  isLicensingNotConfigured,
  licenseBalanceErrorCode,
} from '../api/licensing';
import { errorText } from '../lib/errorText';
import { STATUS, STROKE, SURFACE, TEXT } from '../theme/tokens';

// FD-435 (FD-ADR-041 decision 8) — "Balance M available / this wave reserves N",
// inside the activate dialog.
//
// Design direction: an INLINE STATE BLOCK, the SignInStage detail-block
// precedent — 1px stroke, row fill, no shadow, every numeral tabular. It
// is not a feature area; it is one number an operator needs at the exact moment
// they are about to spend it.
//
// ── THE FETCH POLICY IS THE POINT OF THIS FILE ─────────────────────────────
//
// `useLicenseBalance` is deliberately separate from the presentational panel,
// and it lives at DIALOG level rather than inside `SetupStage`. Three
// consequences, each load-bearing:
//
//   1. It fetches when the DIALOG mounts, which is when the dialog opens —
//      `refetchOnMount: 'always'`, so reopening gets a fresh balance rather
//      than a cached one from ten minutes ago.
//   2. It does NOT refetch when the stage changes. Setup → sign-in → activating
//      is three renders of the same open dialog, and a balance that re-read on
//      each would be three cold-start reads per activation.
//   3. It does NOT refetch on the poll. `useDeviceCodeActivation` drives a
//      settle-then-schedule chain every ~5 s for up to 15 minutes; nothing here
//      is wired to it, and `ActivateWaveDialog.test.tsx` proves that by
//      advancing timers across several poll intervals and requiring the call
//      count to still be 1.
//
// That third property is the one FD-434's own endpoint comment asks the caller
// for and cannot enforce: ATC Licensing runs HA-off with 5–30 s cold starts
// (ATC-ADR-007), so a polled balance read is ~180 calls per activation against a
// service that is cold — a read that becomes an outage for the write beside it
// (FD-ADR-032 §4, applied to a read).
//
// ── AND THE ONE NUMBER THAT LOOKS LIKE A BUG AND IS NOT ────────────────────
//
// `consumable.available` MAY BE NEGATIVE. The 1:1 bucket is advisory, so an
// account can legitimately be over-drawn. It is rendered as an ordinary number
// in primary ink with a plain-English note — never clamped, never red. Painting
// it as an error would say "this is broken" about a state the platform
// deliberately permits.

/**
 * The balance query. Exported so the dialog can own the lifetime while the
 * panel stays presentational — see the fetch-policy note above for why the two
 * are split.
 *
 * `enabled` is load-bearing and is NOT a convenience. `ActivateWaveDialog` is
 * rendered by `WavePanel` **unconditionally** and merely told `open={false}`,
 * so its body runs on every render of the campaign overview. Without this gate
 * the balance would be read on every page load of every campaign — the opposite
 * of "on dialog open", and against an HA-off ATC.
 */
export function useLicenseBalance(enabled: boolean) {
  return useQuery({
    queryKey: ['license-balance'],
    queryFn: getLicenseBalance,
    // Fetch when the dialog opens. Never on a poll, a focus, a reconnect or a
    // re-render. Every one is set EXPLICITLY rather than left to a default,
    // because a default that changes in a future react-query is a default that
    // quietly puts an HA-off ATC read on a five-second loop.
    enabled,
    refetchOnMount: 'always',
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    refetchInterval: false,
    // Zero, deliberately, and it is not a polling knob — nothing here polls.
    // It is what makes REOPENING the dialog re-read rather than render a
    // balance cached from before the last wave was activated.
    staleTime: 0,
    // A cold ATC takes 5–30 s to answer. A retry here would double that wait
    // for an operator who is not blocked by this panel in the first place — the
    // balance is context, and activation proceeds without it.
    retry: false,
  });
}

export interface LicenseBalancePanelProps {
  /**
   * What this activation will reserve, **from the server** — the migration
   * row's `deviceCount`, never a client-side count of anything. `null` when the
   * count is not a usable positive number, in which case no number is claimed.
   */
  reserveCount: number | null;
  balance: LicenseBalance | null | undefined;
  isLoading: boolean;
  error: unknown;
  /**
   * FD-611 — `inline` drops the panel's own box so it can sit as the value of a
   * checklist row (the activation dialog's Advanced details). Content is identical.
   */
  variant?: 'boxed' | 'inline';
}

export default function LicenseBalancePanel({
  reserveCount,
  balance,
  isLoading,
  error,
  variant = 'boxed',
}: LicenseBalancePanelProps) {
  return (
    <div
      className={variant === 'inline' ? 'flex flex-col gap-1' : 'flex flex-col gap-2 p-3'}
      style={
        variant === 'inline'
          ? undefined
          : { border: `1px solid ${STROKE.base}`, backgroundColor: SURFACE.rowFill }
      }
      aria-label="Migration license balance"
    >
      {isLoading ? (
        <BalanceChecking />
      ) : error != null ? (
        <BalanceUnavailable error={error} />
      ) : balance ? (
        <BalanceDetail balance={balance} reserveCount={reserveCount} />
      ) : (
        <p className="font-display text-[13px]" style={{ color: TEXT.secondary }}>
          No license balance was reported for this environment.
        </p>
      )}
    </div>
  );
}

// ——— the check, while it is running ———————————————————————————————————

/**
 * The balance read, in progress (FD-501).
 *
 * This was one static sentence, and the sentence was the whole problem: the read
 * goes to ATC Licensing, which runs HA-off with 5–30 s cold starts
 * (ATC-ADR-007), so an operator opening the activate dialog could sit in front
 * of unchanging text for half a minute with nothing saying the console was still
 * working. Motion is the honest signal there — it distinguishes "waiting on a
 * cold service" from "stuck".
 *
 * INDETERMINATE, deliberately. `ProgressBar` with no `value` animates without
 * claiming a percentage, and there is no percentage to claim: the request is one
 * HTTP call whose duration is a property of whether ATC happens to be warm. A
 * determinate bar here would be the FD-ADR-043 D4 defect in miniature — a
 * control that reports a number nothing computes.
 *
 * It also carries the reason for the wait, because "why is this slow" is the
 * question an operator actually has, and answering it once is cheaper than
 * having them wonder every time.
 */
function BalanceChecking() {
  return (
    <div className="flex flex-col gap-2" data-testid="license-balance-checking">
      <div className="flex items-center gap-3">
        <Spinner size="tiny" />
        <span className="font-display text-[13px]" style={{ color: TEXT.primary }}>
          Checking the migration license balance…
        </span>
      </div>
      <ProgressBar aria-label="Checking the migration license balance" />
      <p className="font-display text-xs" style={{ color: TEXT.secondary }}>
        This reads the balance from the licensing service, which can take a few seconds to wake up.
        Sign-in stays disabled until it answers — or until it fails, which does not stop the wave.
      </p>
    </div>
  );
}

// ——— the balance itself ————————————————————————————————————————————————

function BalanceDetail({
  balance,
  reserveCount,
}: {
  balance: LicenseBalance;
  reserveCount: number | null;
}) {
  const { consumable, pool } = balance;

  // Neither bucket present. ATC answered, and the answer was "nothing here" —
  // which is a different fact from a failed read and reads differently.
  if (!consumable && !pool) {
    return (
      <p className="font-display text-[13px]" style={{ color: TEXT.secondary }}>
        ATC reported no credit balance and no concurrency pool for this environment.
      </p>
    );
  }

  const credits = consumable?.available ?? null;
  // `typeof` rather than truthiness, throughout this file: a balance of exactly
  // 0 is a real, meaningful answer and must render as 0.
  const shortfall =
    typeof credits === 'number' && typeof reserveCount === 'number' && reserveCount > credits;

  return (
    <>
      <p className="font-display text-[13px]" style={{ color: TEXT.primary }}>
        {consumable ? (
          <>
            Balance <Numeral value={consumable.available} /> available
          </>
        ) : (
          <>Balance not reported</>
        )}
        <span style={{ color: TEXT.secondary }}> / this wave reserves </span>
        {typeof reserveCount === 'number' ? (
          <Numeral value={reserveCount} />
        ) : (
          <span style={{ color: TEXT.secondary }}>one per assigned device</span>
        )}
      </p>

      {consumable && (
        <p className="font-display text-xs" style={{ color: TEXT.secondary }}>
          Credits — granted <Numeral value={consumable.granted} subtle /> · burned{' '}
          <Numeral value={consumable.burned} subtle /> · reserved and still open{' '}
          <Numeral value={consumable.reservedOpen} subtle />
        </p>
      )}

      {pool && (
        <p className="font-display text-xs" style={{ color: TEXT.secondary }}>
          Concurrency pool — <Numeral value={pool.inUse} subtle /> of{' '}
          <Numeral value={pool.capacity} subtle /> in use, <Numeral value={pool.available} subtle />{' '}
          free. This is the bucket that blocks: a wave is refused when the pool cannot cover it.
        </p>
      )}

      {/* Over-drawn. An ordinary condition on an advisory bucket, so it is
          stated rather than alarmed about — see the header note. */}
      {typeof credits === 'number' && credits < 0 && (
        <p className="font-display text-xs" style={{ color: TEXT.secondary }}>
          This account is over-drawn on credits. Credits are advisory, so this on its own does not
          stop a wave.
        </p>
      )}

      {/* A shortfall this activation would cause. Amber, because it wants an
          operator's attention — but the copy must not promise the activation
          will proceed: a deployment running LICENSING_ENFORCE_CONSUMABLE=true
          refuses it, and the console cannot see that setting. */}
      {shortfall && (
        <p className="font-display text-xs" style={{ color: STATUS.warning }}>
          This wave reserves more than the credit available. Credits are advisory and the shortfall
          is normally just recorded — but if this deployment enforces them, activation is refused
          and says so.
        </p>
      )}
    </>
  );
}

// ——— a failed read is never a blocker ——————————————————————————————————

function BalanceUnavailable({ error }: { error: unknown }) {
  // `LICENSING_URL` unset. Not broken — licensing is switched off here, and
  // FD-ADR-024's advisory posture means migrations run regardless.
  if (isLicensingNotConfigured(error)) {
    return (
      <p className="font-display text-[13px]" style={{ color: TEXT.secondary }}>
        Licensing is not configured for this deployment, so nothing is reserved or counted.
        Activation is unaffected.
      </p>
    );
  }

  const code = licenseBalanceErrorCode(error);

  return (
    <div className="flex flex-col gap-1">
      <p className="font-display text-[13px]" style={{ color: TEXT.secondary }}>
        The migration license balance could not be read. <strong>Activation still works</strong> —
        the balance is context, not a gate.
      </p>
      <p className="font-display text-xs" style={{ color: TEXT.secondary }}>
        {/* ATC's own code, surfaced rather than swallowed: `account_not_mapped`,
            `licensing_unavailable` and an auth rejection are three different
            operator actions (FD-346). */}
        {code ? (
          <>
            <span className="font-mono text-xs">{code}</span> — {errorText(error)}
          </>
        ) : (
          errorText(error)
        )}
      </p>
    </div>
  );
}

// ——— numerals ——————————————————————————————————————————————————————————

/**
 * A count. `typeof value === 'number'` deliberately, so **0 renders as 0** and
 * only a genuinely absent value reads as "not reported".
 *
 * Coercing with `?? 0` or testing truthiness would make "we were not told" and
 * "we were told zero" look identical — the defect FD-430/FD-432/FD-434 each
 * guarded on their own side, and this is the last link in that chain.
 */
function Numeral({ value, subtle }: { value: number | null | undefined; subtle?: boolean }): ReactNode {
  if (typeof value !== 'number' || !Number.isFinite(value)) {
    return <span style={{ color: TEXT.secondary }}>not reported</span>;
  }
  return (
    <span
      className="font-mono tabular-nums"
      style={{ color: subtle ? TEXT.secondary : TEXT.primary }}
    >
      {value}
    </span>
  );
}
